import { useEffect } from 'react';
import { DEFAULT_DESCRIPTION, SITE_URL, fullTitle, type JsonLd } from '../seo/pages';

function setMeta(selector: string, attr: string, value: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    const [key, val] = selector.replace('meta[', '').replace(']', '').split('=');
    el.setAttribute(key!, val!.replace(/"/g, ''));
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

/**
 * Per-route document title, description, canonical, robots, Open Graph and
 * Twitter tags, and JSON-LD, kept current during client-side navigation.
 * Crawlers and share sheets never see this: they get the static shell the
 * build writes for each public route from src/seo/pages.ts, which carries the
 * same values. Pass the SEO entry's fields for a static route; dynamic pages
 * (a room, a confirmation) pass their own strings.
 */
export function usePageMeta(
  title: string,
  description: string = DEFAULT_DESCRIPTION,
  options: { noindex?: boolean; jsonLd?: readonly JsonLd[] } = {},
): void {
  const { noindex, jsonLd } = options;
  useEffect(() => {
    const full = fullTitle(title);
    document.title = full;
    // The canonical origin, not window.location.origin: a staging or preview
    // host must not advertise itself as the address of the page.
    const url = `${SITE_URL}${window.location.pathname}`;
    setMeta('meta[name="description"]', 'content', description);
    setMeta('meta[property="og:title"]', 'content', full);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:url"]', 'content', url);
    setMeta('meta[name="twitter:title"]', 'content', full);
    setMeta('meta[name="twitter:description"]', 'content', description);
    setMeta('meta[name="robots"]', 'content', noindex ? 'noindex, nofollow' : 'index, follow');

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = url;

    // Replace whatever JSON-LD the shell or the previous route left behind.
    document.head
      .querySelectorAll('script[type="application/ld+json"]')
      .forEach((el) => el.remove());
    for (const block of jsonLd ?? []) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(block);
      document.head.appendChild(script);
    }
  }, [title, description, noindex, jsonLd]);
}
